import moment from 'moment';
import {
  ScheduledPaymentIntervalTypeEnum,
  TScheduledPaymentIntervalType
} from '@/features/payments/types/BatchPayment';
import { DATE_FORMAT_FULL } from '@/utils/dateFormats';

// Ordinal word for the "every Nth <weekday>" phrasing, e.g. intervalValue 14 -> "second" - same
// table the Transfer Money confirmation screen's own schedule text uses (see
// buildTransferScheduleText in features/transferMoney), kept in wording sync with it even though
// this builds a different (more compact) shape for the Manage Payment table.
const WEEK_MULTIPLIER_WORDS: Record<number, string> = {
  2: 'second',
  3: 'third',
  4: 'fourth',
  5: 'fifth',
  6: 'sixth'
};

const getRecurrenceText = (
  dayOfWeek: string,
  intervalType: TScheduledPaymentIntervalType,
  intervalValue?: number
): string => {
  if (
    intervalType === ScheduledPaymentIntervalTypeEnum.EVERY_MONTH_ON_DAY ||
    intervalType === ScheduledPaymentIntervalTypeEnum.LAST_DAY_OF_MONTH
  ) {
    return 'Every month';
  }

  // EVERY_NUMBER_OF_DAYS from here on.
  if (!intervalValue || intervalValue === 1) return 'Every day';

  if (intervalValue % 7 === 0) {
    const multiplier = intervalValue / 7;
    if (multiplier === 1) return `Every ${dayOfWeek}`;

    const word = WEEK_MULTIPLIER_WORDS[multiplier];
    return word ? `Every ${word} ${dayOfWeek}` : `Every ${multiplier} weeks on ${dayOfWeek}`;
  }

  // Not a whole number of weeks - fall back to a plain day count.
  return `Every ${intervalValue} days`;
};

export interface TRecurringDateText {
  /** e.g. "30 October 2026" (one-off/undefined interval) or "23rd October 2026" (recurring). */
  date: string;
  /** e.g. "Every Tuesday" - only set when `intervalType` isn't ONE_OFF/undefined. */
  recurrence?: string;
}

/**
 * Builds the Manage Payment table's Scheduled date cell text - e.g. "30 October 2026" for a
 * one-off row (rendered as a plain, non-ordinal date),
 * or "23rd October 2026" + "Every Tuesday" for a recurring one (rendered as two spans by the
 * caller - see PaymentsTable). Shared by `scheduled_payment`/`pending_scheduled_payment` and
 * `scheduled_transfer` rows alike (batch rows never carry an interval type - a group has no
 * single interval, each payment inside it does - so `intervalType` is undefined for them and
 * they always fall through to the plain-date branch, same as a one-off row).
 */
export const buildRecurringDateText = (
  scheduledDate: string,
  intervalType?: TScheduledPaymentIntervalType,
  intervalValue?: number
): TRecurringDateText => {
  const scheduledMoment = moment(scheduledDate);

  if (!intervalType || intervalType === ScheduledPaymentIntervalTypeEnum.ONE_OFF) {
    return { date: scheduledMoment.format(DATE_FORMAT_FULL) };
  }

  return {
    // moment's default 'en' locale already applies the correct ordinal suffix (1st/2nd/3rd/4th...
    // 21st/22nd/23rd...31st), matching the hand-rolled suffix logic elsewhere in the app
    // (buildTransferScheduleText, PaymentConfirmationForm) without duplicating it here.
    date: scheduledMoment.format('Do MMMM YYYY'),
    recurrence: getRecurrenceText(scheduledMoment.format('dddd'), intervalType, intervalValue)
  };
};
